// SEO Utilities and Schema Generators for Paudhify

const SITE_NAME = 'Paudhify';
const SITE_URL = 'https://paudhify.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

export interface SEOData {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'product' | 'article';
  noIndex?: boolean;
  keywords?: string[];
}

export interface ProductSchema {
  name: string;
  description?: string;
  image: string[];
  price: number;
  discountPrice?: number;
  currency?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  sku?: string;
  brand?: string;
  category?: string;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface ArticleSchema {
  headline: string;
  description?: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
}

// Generate page title with site name
export function generateTitle(pageTitle?: string): string {
  if (!pageTitle) return `${SITE_NAME} - Buy Plants Online in India`;
  return `${pageTitle} | ${SITE_NAME}`;
}

// Generate meta description with length limit
export function generateDescription(description: string, maxLength = 160): string {
  if (description.length <= maxLength) return description;
  return description.substring(0, maxLength - 3).trim() + '...';
}

// Generate canonical URL
export function generateCanonical(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
}

// Generate Organization Schema (JSON-LD)
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description: 'Premium indoor plants and self-watering pots delivered across India',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IN'
    },
    sameAs: [
      'https://www.instagram.com/paudhify',
      'https://www.facebook.com/paudhify'
    ]
  };
}

// Generate WebSite Schema for homepage
export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/shop?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
}

// Generate Product Schema (JSON-LD)
export function generateProductSchema(product: ProductSchema) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: product.image,
    brand: {
      '@type': 'Brand',
      name: product.brand || SITE_NAME
    },
    offers: {
      '@type': 'Offer',
      price: product.discountPrice || product.price,
      priceCurrency: product.currency || 'INR',
      availability: `https://schema.org/${product.availability || 'InStock'}`,
      seller: {
        '@type': 'Organization',
        name: SITE_NAME
      }
    }
  };

  if (product.description) {
    schema.description = product.description;
  }

  if (product.sku) {
    schema.sku = product.sku;
  }

  if (product.category) {
    schema.category = product.category;
  }

  return schema;
}

// Generate Breadcrumb Schema (JSON-LD)
export function generateBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`
    }))
  };
}

// Generate FAQ Schema (JSON-LD)
export function generateFAQSchema(faqs: FAQItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

// Generate Article Schema for plant care pages
export function generateArticleSchema(article: ArticleSchema) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.headline,
    description: article.description,
    image: article.image,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: {
      '@type': 'Organization',
      name: article.author || SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo.png`
      }
    }
  };
}

// Generate LocalBusiness Schema for Indian market
export function generateLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description: 'Premium indoor plants and self-watering pots. Pan-India delivery.',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IN'
    },
    priceRange: '₹₹',
    areaServed: {
      '@type': 'Country',
      name: 'India'
    }
  };
}

// Generate slug from text
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Auto-generate meta description from product data
export function generateProductMetaDescription(
  productName: string,
  price?: number,
  description?: string,
  isStartingPrice?: boolean
): string {
  let priceStr = '';
  if (price && price > 0) {
    priceStr = isStartingPrice ? ` starting from ₹${price}` : ` at ₹${price}`;
  }
  if (description) {
    return generateDescription(
      `Buy ${productName} online${priceStr}. ${description} Free delivery across India. 7-day health guarantee.`
    );
  }
  return generateDescription(
    `Buy ${productName} online${priceStr}. Premium quality indoor plant with self-watering pot. Free delivery across India.`
  );
}

// Auto-generate meta title for product
export function generateProductMetaTitle(productName: string, category?: string): string {
  if (category) {
    return `${productName} - ${category} | Buy Online | ${SITE_NAME}`;
  }
  return `${productName} - Buy Online | ${SITE_NAME}`;
}

export { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE };
